/* eslint-disable jsx-a11y/iframe-has-title */
// src/pages/Kontakt.jsx
import { useState, useEffect } from "react";
import { MapPin, Phone, Mail, Clock, Send, MessageCircle } from "lucide-react";
import Container from "../components/ui/Container";
import Badge from "../components/ui/Badge";

const API_BASE = (process.env.REACT_APP_API_BASE || "").replace(/\/+$/, "");

export default function Kontakt() {
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [message, setMessage] = useState("");
    const [website, setWebsite] = useState(""); // Honeypot – bleibt bei echten Nutzern leer
    const [status, setStatus] = useState("");
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setStatus("");

        try {
            const res = await fetch(`${API_BASE}/api/contact.php`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ name, email, message, website }),
            });

            if(res.ok) {
                const data = await res.json();
                if (data.ok) {
                    setStatus("Erfolgreich gesendet! ✅ Ich melde mich in Kürze.");
                    setName(""); setEmail(""); setMessage("");
                } else throw new Error();
            } else throw new Error();
        } catch {
            setStatus("❌ Senden fehlgeschlagen. Bitte versuche es später erneut oder schreibe direkt an info@tuscherhelmut-betonrohrfallen.de.");
        }
        setLoading(false);
    };

    useEffect(() => {
        document.title = "Kontakt – Tuscher Helmut Betonrohrfallen";
    }, []);

    return (
        <div className="relative overflow-hidden min-h-screen">
            {/* Hintergrund Dekoration */}
            <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
                <div className="absolute top-[10%] -right-[5%] h-[40%] w-[40%] rounded-full bg-brand-500/5 blur-[120px]" />
                <div className="absolute bottom-[10%] -left-[5%] h-[30%] w-[30%] rounded-full bg-blue-500/5 blur-[100px]" />
            </div>

            <Container className="relative py-14 sm:py-20 lg:py-24">
                <div className="text-center max-w-3xl mx-auto mb-16 md:mb-20">
                    <Badge className="mb-4 bg-brand-50 text-brand-600 dark:bg-brand-900/20 border-brand-100/50">Persönliche Beratung</Badge>
                    <h1 className="text-4xl sm:text-6xl font-heading font-black tracking-tight text-slate-900 dark:text-white uppercase">
                        Kontakt <span className="text-brand-600">&amp;</span> Service
                    </h1>
                    <p className="mt-6 text-lg text-slate-600 dark:text-slate-400">
                        Hast du Fragen zum Aufbau oder zur Auswahl der richtigen Falle? Ich berate dich gerne persönlich und unverbindlich.
                    </p>
                </div>

                <div className="grid lg:grid-cols-[1fr_1.2fr] gap-12 lg:gap-20 items-start">
                    {/* ==== Kontaktinfos ==== */}
                    <div className="space-y-8" data-aos="fade-right">
                        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-1">
                            {[
                                { icon: MapPin, label: "Standort", val: "Hofmark 50, 93326 Abensberg" },
                                { icon: Phone, label: "Telefon", val: "+49 (0)163 4194552", href: "tel:+491634194552" },
                                { icon: Mail, label: "E-Mail", val: "info@tuscherhelmut-betonrohrfallen.de", href: "mailto:info@tuscherhelmut-betonrohrfallen.de" },
                                { icon: Clock, label: "Erreichbarkeit", val: "Mo–So: 8:00 – 18:00 Uhr" }
                            ].map((item, i) => (
                                <div key={i} className="flex gap-5 p-6 rounded-[2rem] border border-slate-100 dark:border-slate-800 bg-white/50 dark:bg-slate-900/50 backdrop-blur-sm shadow-sm transition-all hover:shadow-lg">
                                    <div className="h-12 w-12 rounded-2xl bg-brand-50 dark:bg-brand-900/30 flex items-center justify-center text-brand-600 shrink-0 shadow-inner">
                                        <item.icon className="h-6 w-6" />
                                    </div>
                                    <div>
                                        <div className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1">{item.label}</div>
                                        {item.href ? (
                                            <a href={item.href} className="font-bold text-slate-900 dark:text-white hover:text-brand-600 transition-colors break-words">
                                                {item.val}
                                            </a>
                                        ) : (
                                            <div className="font-bold text-slate-900 dark:text-white">{item.val}</div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Karte (Google Maps Iframe) */}
                        <div className="rounded-[2.5rem] overflow-hidden shadow-2xl border border-slate-200 dark:border-slate-800 p-2 bg-white dark:bg-slate-900 group">
                            <div className="rounded-[2rem] overflow-hidden h-[300px] relative">
                                <iframe
                                    width="100%"
                                    height="100%"
                                    frameBorder="0"
                                    scrolling="no"
                                    src="https://maps.google.com/maps?width=612&amp;height=250&amp;hl=de&amp;q=Hofmark%2050%20Abensberg+(Tuscher%20Helmut%20Betonrohrfallen)&amp;t=&amp;z=12&amp;ie=UTF8&amp;iwloc=B&amp;output=embed">
                                </iframe>
                                <div className="absolute inset-0 pointer-events-none ring-1 ring-inset ring-black/5 rounded-[2rem]" />
                            </div>
                        </div>
                    </div>

                    {/* ==== Formular ==== */}
                    <div
                        data-aos="fade-left"
                        className="relative rounded-[3rem] p-8 md:p-12 bg-white dark:bg-slate-900 shadow-2xl shadow-slate-200/50 dark:shadow-none border border-slate-100 dark:border-slate-800"
                    >
                        <div className="flex items-center gap-4 mb-8">
                            <div className="h-10 w-10 rounded-full bg-brand-600 flex items-center justify-center text-white shadow-lg shadow-brand-500/20">
                                <MessageCircle className="h-5 w-5" />
                            </div>
                            <h2 className="text-2xl font-black font-heading text-slate-900 dark:text-white tracking-tight uppercase">Nachricht senden</h2>
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-5">
                            {/* Honeypot gegen Spam-Bots: für Menschen unsichtbar, Bots füllen es aus */}
                            <input
                                type="text"
                                name="website"
                                value={website}
                                onChange={(e) => setWebsite(e.target.value)}
                                tabIndex={-1}
                                autoComplete="off"
                                aria-hidden="true"
                                className="hidden"
                            />
                            <div className="grid sm:grid-cols-2 gap-5">
                                <div className="space-y-1.5">
                                    <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Name</label>
                                    <input
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        placeholder="Max Mustermann"
                                        className="w-full rounded-2xl border border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-950/50 px-5 py-4 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-600 focus:outline-none transition-all outline-none text-slate-900 dark:text-white font-medium"
                                        required
                                    />
                                </div>
                                <div className="space-y-1.5">
                                    <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">E-Mail</label>
                                    <input
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        placeholder="max@beispiel.de"
                                        className="w-full rounded-2xl border border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-950/50 px-5 py-4 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-600 focus:outline-none transition-all outline-none text-slate-900 dark:text-white font-medium"
                                        required
                                    />
                                </div>
                            </div>
                            
                            <div className="space-y-1.5">
                                <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Deine Nachricht</label>
                                <textarea
                                    rows="6"
                                    value={message}
                                    onChange={(e) => setMessage(e.target.value)}
                                    placeholder="Wie kann ich dir helfen?"
                                    className="w-full rounded-[2rem] border border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-950/50 px-5 py-4 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-600 focus:outline-none transition-all outline-none text-slate-900 dark:text-white font-medium"
                                    required
                                />
                            </div>

                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full flex items-center justify-center gap-3 rounded-2xl bg-slate-900 dark:bg-brand-600 text-white px-8 py-5 font-black text-lg uppercase tracking-wider hover:bg-slate-800 dark:hover:bg-brand-500 hover:shadow-xl active:scale-95 transition-all disabled:opacity-50"
                            >
                                {loading ? "Wird gesendet..." : "Nachricht jetzt abschicken"}
                                <Send className="h-5 w-5" />
                            </button>

                            {status && (
                                <div className={`text-center p-4 rounded-xl font-bold text-sm ${status.includes('✅') ? 'bg-emerald-50 text-emerald-700 border border-emerald-100' : 'bg-red-50 text-red-700 border border-red-100'}`}>
                                    {status}
                                </div>
                            )}
                        </form>
                    </div>
                </div>
            </Container>
        </div>
    );
}